"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slider as SliderPrimitive } from "radix-ui";

import { cn } from "@/lib/utils";

const sliderTrackVariants = cva(
  "relative w-full grow overflow-hidden rounded-full",
  {
    variants: {
      variant: {
        default: "h-1 bg-contrast/8",
        light: "h-1 bg-white",
        filter: "h-[3px] bg-contrast/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const sliderRangeVariants = cva("absolute h-full rounded-full", {
  variants: {
    variant: {
      default: "bg-primary",
      light: "bg-white",
      // The filter panel's accent, matching its active pills and count badges.
      filter: "bg-[#ffaf53]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const sliderThumbVariants = cva(
  "block size-3.5 shrink-0 cursor-grab rounded-full transition-all hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:cursor-grabbing disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "border-2 border-primary bg-white shadow-[0_1px_3px_rgba(0,0,0,0.3)]",
        light: "border-0 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.35)]",
        // Hairline border so the white knob still reads against a light rail.
        filter:
          "border border-black/10 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.3)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type SliderVariant = NonNullable<
  VariantProps<typeof sliderTrackVariants>["variant"]
>;

export type SliderProps = React.ComponentProps<typeof SliderPrimitive.Root> &
  VariantProps<typeof sliderTrackVariants> & {
    /**
     * One name per thumb. Required for a range: "slider" tells a screen-reader
     * user nothing about which end they are holding.
     */
    thumbLabels?: string[];
  };

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  variant,
  thumbLabels,
  ...props
}: SliderProps) {
  const _values = value ?? defaultValue ?? [min];

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      data-variant={variant ?? "default"}
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex h-5 w-full touch-none select-none items-center data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={sliderTrackVariants({ variant })}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={sliderRangeVariants({ variant })}
        />
      </SliderPrimitive.Track>
      {_values.map((_, i) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={i}
          aria-label={thumbLabels?.[i]}
          className={sliderThumbVariants({ variant })}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
